import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import usePageMeta from '@/hooks/usePageMeta';
import { getEventById, getRelatedEvents, formatDate, formatViews } from '@/utils/news';
import RelatedList from './components/RelatedList';
import ShareModal from './components/ShareModal';

export default function DetailPage() {
  const { eventId = '' } = useParams();
  const event = getEventById(eventId);
  const [shareOpen, setShareOpen] = useState(false);

  usePageMeta({
    title: event ? `${event.title} - 网红大赛详情 - 每日大瓜网红大赛百度` : '赛事详情 - 每日大瓜网红大赛百度',
    description: event ? event.summary : '网红大赛赛事详情页，查看赛事动态与精彩回顾。',
    keywords: event ? `${event.categoryName},${event.tags.join(',')},网红大赛,每日大瓜` : '网红大赛,赛事详情',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setShareOpen(false);
  }, [eventId]);

  if (!event) {
    return (
      <SiteLayout>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-3xl">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">未找到该赛事详情</h1>
          <p className="mt-2 text-sm text-foreground-500">内容可能已下线，或链接有误。</p>
          <Link
            to="/category/all"
            className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回赛事列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedEvents(event, 5);
  const dynamics = event.dynamics.slice(0, 3);

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <Breadcrumb
          items={[
            { label: event.categoryName, to: `/category/${event.categoryId}` },
            { label: event.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2">
            <header className="text-center max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                <i className="ri-fire-fill text-[10px]"></i>
                {event.categoryName}
              </span>
              <h1 className="mt-3 font-heading font-extrabold text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-tight">
                {event.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {formatDate(event.date)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-map-pin-line"></i>
                  {event.location}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-apps-2-line"></i>
                  {event.categoryName}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-star-line"></i>
                  {event.host}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(event.views)} 阅读
                </span>
              </div>
            </header>

            <div className="mt-6 rounded-lg overflow-hidden bg-secondary-100 h-[260px] md:h-[420px]">
              <img
                src={event.cover}
                alt={`${event.title} 赛事封面`}
                title={`${event.title} - 网红大赛`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-6 md:mt-8">
              <p className="text-base md:text-lg text-foreground-800 leading-relaxed font-medium border-l-4 border-primary-500 pl-4">
                {event.intro}
              </p>
              <div className="mt-6 space-y-4">
                {event.content.map((para, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {para}
                  </p>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {event.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-accent-100 hover:text-accent-900 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-background-200 flex items-center justify-between">
              <span className="text-sm text-foreground-500">觉得有价值？分享给朋友看看</span>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>

            <section className="mt-10">
              <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-flashlight-line text-primary-600"></i>
                赛事动态
              </h2>
              <ul className="rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200">
                {dynamics.map((d, i) => (
                  <li key={d.id}>
                    <Link
                      to={`/detail/${related[i % related.length].id}`}
                      className="flex items-center justify-between gap-4 p-4 hover:bg-background-100 transition-colors"
                    >
                      <span className="text-sm text-foreground-800 line-clamp-1">
                        {d.title}
                      </span>
                      <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-time-line"></i>
                        {formatDate(d.date)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </article>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-5">
              <RelatedList items={related} />
              <div className="rounded-lg border border-background-200 bg-primary-50 p-5 hidden lg:block">
                <h3 className="font-heading font-bold text-base text-foreground-950 mb-2">
                  关注最新赛事
                </h3>
                <p className="text-sm text-foreground-600 mb-4">
                  第一时间获取网红大赛资讯与赛事动态，不错过每一个热点。
                </p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部赛事
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/95 backdrop-blur border-t border-background-200 px-4 py-3 flex items-center gap-3">
        <Link
          to={`/category/${event.categoryId}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-list-check-2"></i>
          同分类
        </Link>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-share-forward-line"></i>
          分享
        </button>
      </div>
      <div className="lg:hidden h-16"></div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={event.title} />
    </SiteLayout>
  );
}